import React, { useEffect, useState } from "react";
import { Col, Row, Stack, Table } from "react-bootstrap";
import { fetchJobs, fetchReportJobs } from "../../../services/httpClient";
import SelectObject from "../../ui/SelectObject";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import { CSVLink } from "react-csv";
import ReactPaginate from "react-paginate";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../constants/table";
import { getCurrentDateTime } from "../../../helpers/date";

interface JobsTableProps {
  data?: any[];
  jobList?: any[];
  showHeader?: boolean;
}

const itemsPerPage = DEFAULT_ITEMS_PER_PAGE;

export const JobsTable = ({ data, jobList, showHeader = false }: JobsTableProps) => {
  const [jobData, setJobData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [selectedJob, setSelectedJob] = useState<number>();
  const [jobs, setJobs] = useState<any[]>([]);
  const [paginate, setPaginate] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(0);

  const pageCount = Math.ceil(filteredData.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentData = filteredData.slice(offset, offset + itemsPerPage);

  const jobHeaders = [
    { label: "Department", key: "department" },
    { label: "Job", key: "job" },
    { label: "Case Name", key: "name" },
    { label: "First Name", key: "firstname" },
    { label: "Last Name", key: "lastname" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (data && jobList) {
        setJobData(data);
        setFilteredData(data);
        setJobs(jobList);
      } else {
        await getJobData();
      }
    };

    fetchData();
  }, [data, jobList]);

  const getJobData = async () => {
    fetchReportJobs()
      .then((res: any) => {
        const reportJobs = res[0].data;
        setJobData(reportJobs);
        setFilteredData(reportJobs);

        const deptCounts = reportJobs.reduce((acc: Record<string, number>, job: any) => {
          const dept = job.department;
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {});

        fetchJobs()
          .then((res: any) => {
            const jobs = res[0].data;
            const deptMap = new Map<string, any>();
            jobs.forEach((j: any) => {
              const deptName = j.department;
              const count = deptCounts[deptName] ?? 0;
              if (!deptMap.has(deptName)) {
                deptMap.set(deptName, { name: `${deptName} (${count})`, key: deptName, count });
              }
            });

            Object.entries(deptCounts).forEach(([dept, count]) => {
              const key = dept;
              if (!deptMap.has(key)) {
                deptMap.set(key, { name: `${key} (${count})`, key, count });
              } else {
                const existing = deptMap.get(key);
                existing.count = count;
                existing.name = `${key} (${count})`;
              }
            });

            const departments = Array.from(deptMap.values()).sort((a: any, b: any) => a.key.localeCompare(b.key));
            setJobs(departments);
          })
          .catch((err: any) => {
            console.log(err);
          });
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const updateJob = (e: any) => {
    setSelectedJob(e.target.value);
    const filtered = jobData.filter((d: any) => {
      if (e.target.value === "0") {
        return d;
      } else {
        return d.department === e.target.value;
      }
    });

    setFilteredData(filtered);
    setCurrentPage(0);

    e.target.value === "0" ? setPaginate(true) : setPaginate(false);
  };

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected);
  };

  return (
    <>
      {showHeader && (
        <Row>
          <Col>
            <h5 className="app-header-text">Current Job Assignments</h5>
          </Col>
        </Row>
      )}
      {jobData?.length > 0 ? (
        <>
          <Row id="jobs-filters">
            <Col xs={4} className="mb-1">
              <SelectObject
                label="Department (# People)"
                initialOption="View All"
                value={selectedJob}
                options={jobs}
                onUpdate={(e: any) => updateJob(e)}
              />
            </Col>
            <Col xs={4} className="d-flex flex-column text-end justify-content-end align-items-end">
              <Stack direction="horizontal" gap={2} className="mb-3">
                <CSVLink data={jobData} headers={jobHeaders} filename={`job_assignments${getCurrentDateTime()}.csv`} target="_blank">
                  <IconEmbeddedButton tooltipText="Data Export" icon={faDownload} />
                </CSVLink>
              </Stack>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12}>
              <Table striped>
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Job</th>
                    <th>Case Name</th>
                    <th>Person</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate ? (
                    currentData?.map((d: any, index: number) => (
                      <tr key={index}>
                        <td>{d.department}</td>
                        <td>{d.job}</td>
                        <td className="first-letter">{d.name}</td>
                        <td>
                          {d.firstname} {d.lastname}
                        </td>
                      </tr>
                    ))
                  ) : filteredData.length === 0 ? (
                    <p className="mt-2">No Job Records</p>
                  ) : (
                    filteredData?.map((d: any, index: number) => (
                      <tr key={index}>
                        <td>{d.department}</td>
                        <td>{d.job}</td>
                        <td className="first-letter">{d.name}</td>
                        <td>
                          {d.firstname} {d.lastname}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {paginate && pageCount > 1 && (
                <div id={"jobs-pagination"}>
                  <ReactPaginate
                    previousLabel={"← Previous"}
                    nextLabel={"Next →"}
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination justify-content-center"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    activeClassName={"active"}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </Col>
          </Row>
        </>
      ) : (
        <p className="mt-2">No Job Records</p>
      )}
    </>
  );
};
